import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Code, 
  Search, 
  Filter, 
  Plus,
  Eye,
  Edit,
  Trash2,
  Star,
  Timer,
  Target,
  Tag,
  PlayCircle,
  Loader,
  CheckCircle,
  AlertCircle,
  BarChart3,
  User,
  Share2,
  Users
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import CreateCodingChallengeModal from '@/components/coding-challenges/CreateCodingChallengeModal'
import ShareChallengeModal from '@/components/coding-challenges/ShareChallengeModal'

interface CodingProblem {
  id: string
  title: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  category: string
  tags: string[]
  time_limit: number
  memory_limit: number
  allowed_languages: string[]
  examples: any[]
  test_cases: any[]
  is_active: boolean
  created_at: string
  created_by: number
  accessType?: 'owned' | 'shared'
  permissionLevel?: 'view' | 'edit'
  canEdit?: boolean
  shareType?: 'created' | 'shared_individual' | 'shared_team'
  sharedBy?: string
  sharedByName?: string
}

export default function CodingChallengesPage() {
  const navigate = useNavigate()
  const [problems, setProblems] = useState<CodingProblem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [createdByFilter, setCreatedByFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState('created_at')
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedProblem, setSelectedProblem] = useState<CodingProblem | null>(null)
  const codingChallengesEnabled = useFeatureFlag('recruiterCodingChallenges')

  useEffect(() => {
    if (!codingChallengesEnabled) return
    loadProblems()
  }, [difficultyFilter, categoryFilter, createdByFilter, searchQuery, sortBy, codingChallengesEnabled])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser))
      } catch (err) {
        console.warn('Failed to parse currentUser from storage', err)
        setCurrentUser(null)
      }
    } else {
      setCurrentUser(null)
    }
  }, [])

  const loadProblems = async () => {
    if (!codingChallengesEnabled) return
    try {
      setLoading(true)
      
      const params: any = {}
      if (difficultyFilter !== 'all') params.difficulty = difficultyFilter
      if (categoryFilter !== 'all') params.category = categoryFilter
      if (createdByFilter !== 'all') params.createdBy = createdByFilter
      if (searchQuery) params.search = searchQuery

      const response = await apiClient.getCodingProblems(params)
      if (response.success) {
        setProblems(response.data?.problems || [])
      } else {
        setProblems([])
      }
    } catch (err) {
      console.error('Failed to load coding problems:', err)
      setProblems([])
    } finally {
      setLoading(false)
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'hard':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return <CheckCircle className="w-4 h-4" />
      case 'medium':
        return <AlertCircle className="w-4 h-4" />
      case 'hard':
        return <Target className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const handleViewProblem = (problemId: string) => {
    navigate(`/recruiter/coding-challenges/${problemId}`)
  }

  const handleEditProblem = (problemId: string) => {
    navigate(`/recruiter/coding-challenges/${problemId}/edit`)
  }

  const handleTestProblem = (problemId: string) => {
    navigate(`/recruiter/coding-challenges/${problemId}/test`)
  }

  const handleCreateProblem = () => {
    setShowCreateModal(true)
  }

  const handleProblemCreated = () => {
    setShowCreateModal(false)
    loadProblems()
  }

  const canEditProblem = (problem: CodingProblem) => {
    if (!currentUser) return false
    if (problem.canEdit !== undefined) return problem.canEdit
    return problem.created_by === currentUser.id || currentUser.role === 'admin'
  }
  
  const canShareProblem = (problem: CodingProblem) => {
    if (!currentUser) return false
    return problem.created_by === currentUser.id || problem.accessType === 'owned'
  }
  
  const handleShareProblem = (problem: CodingProblem) => {
    setSelectedProblem(problem)
    setShowShareModal(true)
  }

  const categories = [...new Set((problems || []).map(p => p.category))]

  if (!codingChallengesEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Coding challenges coming soon</h1>
          <p className="text-gray-600 mt-2 max-w-lg">
            This module is disabled in the current environment while we finish preparing it for production.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading coding challenges...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coding Challenges</h1>
          <p className="text-gray-600 mt-1">Create and manage coding problems for technical interviews</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button onClick={handleCreateProblem} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Problem
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Code className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{(problems || []).length}</div>
              <div className="text-sm text-gray-600">Total Problems</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(problems || []).filter(p => p.difficulty === 'easy').length}
              </div>
              <div className="text-sm text-gray-600">Easy</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(problems || []).filter(p => p.difficulty === 'medium').length}
              </div>
              <div className="text-sm text-gray-600">Medium</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(problems || []).filter(p => p.difficulty === 'hard').length}
              </div>
              <div className="text-sm text-gray-600">Hard</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search problems..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
              />
            </div>
          </div>
          
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          >
            <option value="all">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={createdByFilter}
            onChange={(e) => setCreatedByFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          >
            <option value="all">All Problems</option>
            <option value="me">Created by Me</option>
            <option value="shared">Shared with Me</option>
            <option value="team">Created by My Team</option>
          </select>
        </div>
      </div>

      {/* Problems List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {(problems || []).length} Coding Problems
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {!problems || problems.length === 0 ? (
            <div className="text-center py-12">
              <Code className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No coding problems</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first coding challenge.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleCreateProblem}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Problem
                </button>
              </div>
            </div>
          ) : (
            (problems || []).map((problem) => (
              <motion.div
                key={problem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Code className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">{problem.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                          {getDifficultyIcon(problem.difficulty)}
                          <span className="ml-1 capitalize">{problem.difficulty}</span>
                        </span>
                        {problem.shareType === 'created' || (currentUser && problem.created_by === currentUser.id) ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                            <User className="w-3 h-3 mr-1" />
                            Created by you
                          </span>
                        ) : problem.shareType === 'shared_team' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                            <Users className="w-3 h-3 mr-1" />
                            Shared with team
                            {problem.sharedByName && <span className="ml-1 text-gray-500">by {problem.sharedByName}</span>}
                          </span>
                        ) : problem.shareType === 'shared_individual' || problem.accessType === 'shared' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">
                            <Share2 className="w-3 h-3 mr-1" />
                            Shared individually
                            {problem.sharedByName && <span className="ml-1 text-gray-500">by {problem.sharedByName}</span>}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{problem.description}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center space-x-1">
                          <Tag className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{problem.category}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Timer className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{problem.time_limit}s</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <BarChart3 className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{problem.test_cases?.length || 0} tests</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${problem.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {problem.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewProblem(problem.id)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50"
                        title="View Problem"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTestProblem(problem.id)}
                        className="text-green-600 hover:text-green-900 p-2 rounded hover:bg-green-50"
                        title="Test Problem"
                      >
                        <PlayCircle className="w-4 h-4" />
                      </button>
                      {canEditProblem(problem) && (
                        <button
                          onClick={() => handleEditProblem(problem.id)}
                          className="text-orange-600 hover:text-orange-900 p-2 rounded hover:bg-orange-50"
                          title="Edit Problem"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canShareProblem(problem) && (
                        <button
                          onClick={() => handleShareProblem(problem)}
                          className="text-purple-600 hover:text-purple-900 p-2 rounded hover:bg-purple-50"
                          title="Share Problem"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Tags */}
                {problem.tags && problem.tags.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {problem.tags.slice(0, 5).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded">
                          {tag}
                        </span>
                      ))}
                      {problem.tags.length > 5 && (
                        <span className="text-xs text-gray-500">+{problem.tags.length - 5} more</span>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Create Problem Modal */}
      <CreateCodingChallengeModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProblemCreated={handleProblemCreated}
      />

      {/* Share Challenge Modal */}
      <ShareChallengeModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        challenge={selectedProblem}
        onShared={() => {
          setShowShareModal(false)
          loadProblems()
        }}
      />
    </div>
  )
}
